"use client";

import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./TourTooltip.module.css";

/**
 * Positioned tooltip that appears next to a target element.
 * Renders a card with content, optional button, and a CSS arrow.
 */
export default function TourTooltip({
  targetSelector,
  placement = "bottom", // "top" | "bottom" | "left" | "right"
  children,
  padding = 12,
  offset = 16,
}) {
  const [pos, setPos] = useState(null);
  const [portalTarget, setPortalTarget] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  useEffect(() => {
    const measure = () => {
      if (!targetSelector) return;
      const el = document.querySelector(targetSelector);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const tooltipEl = tooltipRef.current;
      const tw = tooltipEl?.offsetWidth || 320;
      const th = tooltipEl?.offsetHeight || 100;

      let top, left;
      switch (placement) {
        case "bottom":
          top = r.bottom + padding + offset;
          left = r.left + r.width / 2 - tw / 2;
          break;
        case "top":
          top = r.top - padding - offset - th;
          left = r.left + r.width / 2 - tw / 2;
          break;
        case "left":
          top = r.top + r.height / 2 - th / 2;
          left = r.left - padding - offset - tw;
          break;
        case "right":
          top = r.top + r.height / 2 - th / 2;
          left = r.right + padding + offset;
          break;
        default:
          top = r.bottom + padding + offset;
          left = r.left + r.width / 2 - tw / 2;
      }

      // Clamp to viewport
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (left < 16) left = 16;
      if (left + tw > vw - 16) left = vw - 16 - tw;
      if (top < 16) top = 16;
      if (top + th > vh - 16) top = vh - 16 - th;

      setPos({ top, left });
    };

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    // Re-measure after a short delay to account for transitions
    const timer = setTimeout(measure, 350);

    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      clearTimeout(timer);
    };
  }, [targetSelector, placement, padding, offset]);

  if (!portalTarget) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      data-tour-tooltip=""
      className={`${styles.tooltip} ${styles[`arrow_${placement}`]}`}
      style={pos ? { top: pos.top, left: pos.left, opacity: 1 } : { opacity: 0 }}
    >
      {children}
    </div>,
    portalTarget,
  );
}
